export interface TrackingService {
  id: string;
  label: string;
  placeholder: string;
  idLabel: string;
  validationRegex: RegExp;
  validationHint: string;
  detectionRegex: RegExp;
  idExtractionRegex: RegExp;
  color: string;
  icon: string;
  description: string;
  instructions: string;
  findIdSteps: string[];
  dashboardUrl: string;
  verifySteps: string[];
  notes?: string;
  bestPractices: string[];
  fn: (id: string) => string;
}

export const TRACKING_SERVICES: TrackingService[] = [
  {
    id: 'ga4',
    label: 'Google Analytics 4',
    placeholder: 'G-XXXXXXXXXX',
    idLabel: 'Measurement ID',
    validationRegex: /^G-[A-Z0-9]{4,10}$/,
    validationHint: 'Formato: G- seguito da 4-10 caratteri alfanumerici (es. G-ABC123DEF4)',
    detectionRegex: /gtag\('config',\s*'(G-[A-Z0-9]+)'\)/,
    idExtractionRegex: /gtag\('config',\s*'(G-[A-Z0-9]+)'\)/,
    color: '#4285F4',
    icon: '📊',
    description: 'Servizio gratuito di Google per analizzare il traffico web, comportamento utenti e conversioni. GA4 offre analisi avanzate basate su eventi, machine learning integrato e integrazione con Google Ads.',
    instructions: 'Google Analytics (analytics.google.com) > Amministrazione > Flussi di dati > seleziona il flusso web > copia l\'ID misurazione',
    findIdSteps: [
      'Vai su analytics.google.com e accedi con il tuo account Google',
      'Clicca sull\'icona ingranaggio "Amministrazione" in basso a sinistra',
      'Nella colonna "Proprietà", clicca su "Flussi di dati"',
      'Seleziona il flusso web del tuo sito (o creane uno nuovo)',
      'Copia l\'ID misurazione in alto a destra (formato G-XXXXXXXXXX)',
    ],
    dashboardUrl: 'https://analytics.google.com/',
    verifySteps: [
      'Nella console GA4, vai su Rapporti > Tempo reale',
      'Visita il tuo sito in un\'altra tab del browser',
      'Entro 30 secondi dovresti vedere la visita apparire nel report in tempo reale',
      'In alternativa, installa l\'estensione Chrome "Google Analytics Debugger"',
    ],
    bestPractices: [
      'Configura obiettivi di conversione per CTA e form',
      'Abilita Enhanced Measurement per tracciare scroll e outbound click automaticamente',
      'Collega Google Ads per attribuzione campagne',
    ],
    fn: (id: string) => `<!-- Google Analytics 4 -->\n<script async src="https://www.googletagmanager.com/gtag/js?id=${id}"></script>\n<script>\n  window.dataLayer = window.dataLayer || [];\n  function gtag(){dataLayer.push(arguments);}\n  gtag('js', new Date());\n  gtag('config', '${id}');\n</script>`,
  },
  {
    id: 'gtm',
    label: 'Google Tag Manager',
    placeholder: 'GTM-XXXXXXX',
    idLabel: 'Container ID',
    validationRegex: /^GTM-[A-Z0-9]{4,10}$/,
    validationHint: 'Formato: GTM- seguito da lettere e numeri (es. GTM-ABC1234)',
    detectionRegex: /['"](GTM-[A-Z0-9]{4,10})['"]/,
    idExtractionRegex: /['"](GTM-[A-Z0-9]{4,10})['"]/,
    color: '#246FDB',
    icon: '🏷️',
    description: 'Container di tag management di Google. Permette di gestire tutti i tag di tracking (GA4, Meta Pixel, ecc.) da un\'unica interfaccia senza modificare il codice del sito.',
    instructions: 'Google Tag Manager (tagmanager.google.com) > seleziona il contenitore > l\'ID è visibile in alto a destra',
    findIdSteps: [
      'Vai su tagmanager.google.com e accedi con il tuo account Google',
      'Seleziona il contenitore del tuo sito (o creane uno nuovo)',
      'L\'ID del contenitore è visibile in alto a destra nel formato GTM-XXXXXXX',
      'Copialo e incollalo qui sotto',
    ],
    dashboardUrl: 'https://tagmanager.google.com/',
    verifySteps: [
      'In GTM, clicca "Anteprima" per attivare la Preview Mode',
      'Inserisci l\'URL del tuo sito e clicca "Connect"',
      'Si aprirà il Tag Assistant che mostra quali tag si attivano',
      'Verifica che tutti i tag configurati si attivino correttamente',
    ],
    notes: 'Lo snippet GTM inserisce solo la parte <script> nel <head>. Per funzionalità complete (es. remarketing), GTM richiede anche un tag <noscript> subito dopo <body>. Questo verrà gestito automaticamente se il tuo sito lo supporta.',
    bestPractices: [
      'Usa GTM per gestire centralmente tutti i tag di tracking',
      'Configura trigger personalizzati per eventi specifici',
      'Usa il Data Layer per passare dati strutturati ai tag',
    ],
    fn: (id: string) => `<!-- Google Tag Manager -->\n<script>(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${id}');</script>\n<!-- End Google Tag Manager -->`,
  },
  {
    id: 'meta',
    label: 'Meta Pixel',
    placeholder: '1234567890123456',
    idLabel: 'Pixel ID',
    validationRegex: /^\d{10,20}$/,
    validationHint: 'Il Pixel ID è un numero di 10-20 cifre (es. 1234567890123456)',
    detectionRegex: /fbq\('init',\s*'(\d+)'\)/,
    idExtractionRegex: /fbq\('init',\s*'(\d+)'\)/,
    color: '#1877F2',
    icon: '📘',
    description: 'Pixel di tracciamento di Meta per misurare le conversioni dalle campagne Facebook e Instagram Ads, e creare audience di retargeting basate sui visitatori del sito.',
    instructions: 'Facebook Events Manager (business.facebook.com/events_manager) > Origini dei dati > seleziona il Pixel > Impostazioni > Pixel ID',
    findIdSteps: [
      'Vai su business.facebook.com/events_manager',
      'Nella sezione "Origini dei dati", seleziona il tuo Pixel (o creane uno nuovo)',
      'Clicca su "Impostazioni" nel menu laterale',
      'Il Pixel ID è il numero visualizzato in alto (es. 1234567890123456)',
      'Copialo e incollalo qui sotto',
    ],
    dashboardUrl: 'https://business.facebook.com/events_manager/',
    verifySteps: [
      'Installa l\'estensione Chrome "Meta Pixel Helper"',
      'Visita il tuo sito — l\'estensione mostrerà un badge verde se il pixel è attivo',
      'In Events Manager, vai su "Test Events" per verificare gli eventi ricevuti',
      'Il PageView viene tracciato automaticamente su ogni caricamento',
    ],
    bestPractices: [
      'Configura eventi personalizzati per azioni chiave (Lead, Purchase, CompleteRegistration)',
      'Usa Conversions API per tracking server-side più accurato',
      'Crea audience personalizzate basate sui visitatori per campagne di retargeting',
    ],
    fn: (id: string) => `<!-- Meta Pixel Code -->\n<script>!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${id}');fbq('track','PageView');</script>\n<noscript><img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=${id}&ev=PageView&noscript=1"/></noscript>`,
  },
  {
    id: 'linkedin',
    label: 'LinkedIn Insight',
    placeholder: '1234567',
    idLabel: 'Partner ID',
    validationRegex: /^\d{5,10}$/,
    validationHint: 'Il Partner ID è un numero di 5-10 cifre (es. 1234567)',
    detectionRegex: /_linkedin_partner_id\s*=\s*"(\d+)"/,
    idExtractionRegex: /_linkedin_partner_id\s*=\s*"(\d+)"/,
    color: '#0A66C2',
    icon: '💼',
    description: 'Tag di tracciamento di LinkedIn per misurare conversioni dalle campagne LinkedIn Ads e creare audience di retargeting professionale basate sui visitatori del sito.',
    instructions: 'LinkedIn Campaign Manager > Impostazioni account > Insight Tag > copia il Partner ID',
    findIdSteps: [
      'Vai su linkedin.com/campaignmanager',
      'Seleziona il tuo account pubblicitario',
      'Vai su "Impostazioni account" nel menu',
      'Clicca su "Insight Tag"',
      'Copia il Partner ID visualizzato (un numero di 5-10 cifre)',
    ],
    dashboardUrl: 'https://www.linkedin.com/campaignmanager/',
    verifySteps: [
      'Installa l\'estensione Chrome "LinkedIn Insight Tag Helper"',
      'Visita il tuo sito — l\'estensione confermerà se il tag è installato correttamente',
      'In Campaign Manager, vai su Analisi > Insight Tag per vedere lo stato',
      'I dati di audience iniziano a popolarsi entro 24-48 ore',
    ],
    bestPractices: [
      'Configura conversioni per form submit e azioni chiave',
      'Usa Matched Audiences per retargeting su LinkedIn',
      'Monitora la demografica professionale dei visitatori',
    ],
    fn: (id: string) => `<!-- LinkedIn Insight Tag -->\n<script type="text/javascript">_linkedin_partner_id="${id}";window._linkedin_data_partner_ids=window._linkedin_data_partner_ids||[];window._linkedin_data_partner_ids.push(_linkedin_partner_id);</script><script type="text/javascript">(function(l){if(!l){window.lintrk=function(a,b){window.lintrk.q.push([a,b])};window.lintrk.q=[]}var s=document.getElementsByTagName("script")[0];var b=document.createElement("script");b.type="text/javascript";b.async=true;b.src="https://snap.licdn.com/li.lms-analytics/insight.min.js";s.parentNode.insertBefore(b,s);})(window.lintrk);</script>`,
  },
  {
    id: 'hotjar',
    label: 'Hotjar',
    placeholder: '1234567',
    idLabel: 'Site ID',
    validationRegex: /^\d{5,10}$/,
    validationHint: 'Il Site ID è un numero di 5-10 cifre (es. 1234567)',
    detectionRegex: /hjid:\s*(\d+)/,
    idExtractionRegex: /hjid:\s*(\d+)/,
    color: '#FF3C00',
    icon: '🔥',
    description: 'Strumento di analisi comportamentale che registra heatmap (dove cliccano gli utenti), registrazioni di sessioni reali, e sondaggi feedback per capire come gli utenti interagiscono con il sito.',
    instructions: 'Hotjar (hotjar.com) > Settings > Sites & Organizations > il Site ID è visibile accanto al nome del sito',
    findIdSteps: [
      'Vai su hotjar.com e accedi al tuo account',
      'Clicca sull\'icona ingranaggio "Settings"',
      'Vai su "Sites & Organizations"',
      'Il Site ID è il numero visibile accanto al nome del tuo sito',
      'Copialo e incollalo qui sotto',
    ],
    dashboardUrl: 'https://www.hotjar.com/',
    verifySteps: [
      'Dopo l\'installazione, visita il tuo sito',
      'Nella dashboard Hotjar, controlla se appare il badge "Tracking Active"',
      'Le prime heatmap e registrazioni appariranno entro pochi minuti',
      'Usa la sezione "Recordings" per vedere le sessioni registrate',
    ],
    bestPractices: [
      'Analizza le heatmap per ottimizzare il posizionamento delle CTA',
      'Guarda le registrazioni per identificare problemi di UX',
      'Usa i sondaggi feedback per raccogliere input diretto dagli utenti',
    ],
    fn: (id: string) => `<!-- Hotjar Tracking Code -->\n<script>(function(h,o,t,j,a,r){h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};h._hjSettings={hjid:${id},hjsv:6};a=o.getElementsByTagName('head')[0];r=o.createElement('script');r.async=1;r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;a.appendChild(r);})(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');</script>`,
  },
];

export interface DetectedTracking {
  serviceId: string;
  trackingId: string;
  service: TrackingService;
}

export function detectActiveTrackingServices(customHeadCode: string): DetectedTracking[] {
  if (!customHeadCode) return [];
  const results: DetectedTracking[] = [];
  for (const service of TRACKING_SERVICES) {
    const match = customHeadCode.match(service.idExtractionRegex);
    if (match && match[1]) {
      results.push({
        serviceId: service.id,
        trackingId: match[1],
        service,
      });
    }
  }
  return results;
}

export function removeTrackingSnippet(customHeadCode: string, serviceId: string): string {
  const removalPatterns: Record<string, RegExp> = {
    ga4: /<!-- Google (Analytics 4|tag \(gtag\.js\)) -->[\s\S]*?<\/script>\s*<script>[\s\S]*?<\/script>/g,
    gtm: /<!-- Google Tag Manager -->[\s\S]*?<!-- End Google Tag Manager -->/g,
    meta: /<!-- Meta Pixel Code -->[\s\S]*?<\/noscript>/g,
    linkedin: /<!-- LinkedIn Insight Tag -->[\s\S]*?<\/script>\s*<script[\s\S]*?<\/script>/g,
    hotjar: /<!-- Hotjar Tracking Code -->[\s\S]*?<\/script>/g,
  };
  const pattern = removalPatterns[serviceId];
  if (!pattern) return customHeadCode;
  return customHeadCode.replace(pattern, '').replace(/\n{3,}/g, '\n\n').trim();
}

